// Detail článku (issue #34, screens/21-clanek.md, mockupy
// design/„Detail článku.dc.html“ + „Detail článku mobil.dc.html“).
//
// `body.type-post`, wrapper `.news-item-detail` se schema.org metadaty:
//   meta[itemprop=datePublished] · span[itemprop=image] > meta[itemprop=url]
//   span[itemprop=author] > meta[itemprop=name]
//   article > header > h1[itemprop=headline]
//           > .text            (WYSIWYG redakce: p / h2 / h3 / ul / blockquote / img)
//           > footer > .next-prev > a[data-testid=buttonPreviousArticle|buttonNextArticle]
//
// Restylujeme nativní text a dodáváme, co v DOM není:
//   • HLAVNÍ VIZUÁL — v DOM není `<img>`, jen `meta[itemprop=url]` (otevřený bod
//     handoffu č. 6); vytáhne ho JS. Bez JS layout drží, fotka prostě odpadne.
//   • doba čtení (odhad z délky celého textu) a „Obsah článku“ (kotvy z H2),
//   • karty prev/next (nativní odkazy mají jen obecný text „Předchozí článek“ —
//     titulek doplníme z výpisu rubriky, když se ho podaří dotáhnout),
//   • pás „Další z rubriky“ (fetch výpisu rubriky z breadcrumbu),
//   • sdílení (Shoptet v blank módu žádné sdílecí odkazy nerenderuje).
//
// VYPUŠTĚNO proti designu (data nejsou, nevymýšlíme je): box „Z článku rovnou
// do dílny“ (souvisejících produktů článek nemá — spec je má jako redakční blok)
// a štítky článku (Shoptet u článků nativní tagy nevede).

import { icon } from './icon.js';
import { currentRubric, formatDate, parseArticles, readLabel, textOf } from './blogModel.js';
import { firstName } from './headerTools.js';
import L from '../locale/cs/articleDetail.js';

const READY = 'bs-post-ready';

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_AUTHOR_NOTE = L.authorNote;
const LABEL_SHARE = L.share;
const LABEL_SHARE_ONE = L.shareOne;
const LABEL_COPIED = L.copied;
const LABEL_TOC = L.toc;
const LABEL_PREV = L.prev;
const LABEL_NEXT = L.next;
const LABEL_MORE_PRE = L.morePre;
const LABEL_MORE_ALL = L.moreAll;

const MORE_LIMIT = 3;
const COPIED_MS = 1800;

const SHARE = [
  { key: 'fb', icon: 'chat', label: L.shareFb },
  { key: 'mail', icon: 'mail', label: L.shareMail },
  { key: 'copy', icon: 'file', label: L.shareCopy },
  { key: 'print', icon: 'info', label: L.sharePrint },
];

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function link(href, cls, txt) {
  const node = el('a', cls, txt);
  node.href = href;
  return node;
}

function metaContent(sel) {
  const node = document.querySelector(sel);
  return node ? node.getAttribute('content') : null;
}

function isPost() {
  return document.body.classList.contains('type-post');
}

// ---------------------------------------------------------------------------
// Hlavička: rubrika + datum + doba čtení → H1 → perex → autor a sdílení
// ---------------------------------------------------------------------------
function buildMeta(head, rubric, text) {
  const row = el('div', 'bs-post__meta');
  if (rubric) {
    row.appendChild(link(rubric.href, 'bs-post__chip', rubric.name));
  }
  const date = formatDate(metaContent('.news-item-detail meta[itemprop="datePublished"]'));
  if (date) row.appendChild(el('span', 'bs-post__date', date));
  const read = readLabel(text ? textOf(text) : '');
  if (read) {
    if (date) row.appendChild(el('span', 'bs-post__dot'));
    row.appendChild(el('span', 'bs-post__read', read));
  }
  if (row.children.length) head.insertAdjacentElement('afterbegin', row);
}

// Perex = první odstavec redakčního textu; STĚHUJE se nad hlavní vizuál.
function movePerex(head, text) {
  const first = text.querySelector(':scope > p');
  if (!first) return;
  const value = textOf(first);
  if (value.length < 40) return; // krátký řádek není perex
  first.classList.add('bs-post__perex');
  head.appendChild(first);
}

function shareUrl(key) {
  const url = window.location.href;
  if (key === 'fb') return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url);
  if (key === 'mail') {
    return 'mailto:?subject=' + encodeURIComponent(document.title) + '&body=' + encodeURIComponent(url);
  }
  return '#';
}

function copyLink(btn) {
  const done = function () {
    btn.classList.add('is-done');
    btn.setAttribute('aria-label', LABEL_COPIED);
    window.setTimeout(function () {
      btn.classList.remove('is-done');
      btn.setAttribute('aria-label', L.shareCopy);
    }, COPIED_MS);
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(window.location.href).then(done, function () {});
  }
}

function buildShare() {
  const box = el('div', 'bs-post__share');
  box.appendChild(el('span', 'bs-post__share-label', LABEL_SHARE));

  for (let i = 0; i < SHARE.length; i += 1) {
    const def = SHARE[i];
    const isAction = def.key === 'copy' || def.key === 'print';
    const node = isAction ? el('button', 'bs-post__sbtn') : link(shareUrl(def.key), 'bs-post__sbtn');
    node.setAttribute('aria-label', def.label);
    if (isAction) node.type = 'button';
    else if (def.key === 'fb') {
      node.target = '_blank';
      node.rel = 'noopener';
    }
    node.appendChild(icon(def.icon));
    if (def.key === 'copy') {
      node.addEventListener('click', function () {
        copyLink(node);
      });
    }
    if (def.key === 'print') {
      node.addEventListener('click', function () {
        window.print();
      });
    }
    box.appendChild(node);
  }

  // mobil: jedno tlačítko (nativní share sheet, fallback = kopie odkazu)
  const one = el('button', 'bs-post__sone');
  one.type = 'button';
  one.setAttribute('aria-label', LABEL_SHARE_ONE);
  one.appendChild(icon('arrow-right'));
  one.addEventListener('click', function () {
    if (navigator.share) {
      navigator.share({ title: document.title, url: window.location.href }).catch(function () {});
    } else {
      copyLink(one);
    }
  });
  box.appendChild(one);
  return box;
}

function buildByline(head) {
  const author = metaContent('.news-item-detail span[itemprop="author"] meta[itemprop="name"]');
  const row = el('div', 'bs-post__byline');

  const who = el('div', 'bs-post__author');
  const mark = el('span', 'bs-post__avatar');
  mark.appendChild(icon('user'));
  who.appendChild(mark);
  const lines = el('span', 'bs-post__author-lines');
  // jen krestni jmeno (zadani 2026-09-10); fallback je popis redakce, ne osoba
  lines.appendChild(el('span', 'bs-post__author-name', author ? firstName(author) : L.authorFallback));
  lines.appendChild(el('span', 'bs-post__author-note', LABEL_AUTHOR_NOTE));
  who.appendChild(lines);
  row.appendChild(who);

  row.appendChild(buildShare());
  head.appendChild(row);
}

// ---------------------------------------------------------------------------
// Hlavní vizuál — v DOM není `<img>`, bereme URL ze schema meta
// ---------------------------------------------------------------------------
// Sedí uvnitř hlavičky, pořadí proti autorskému řádku přepíná `order` v CSS
// (desktop = pod autorem, mobil = nad ním) — jeden markup, žádné stěhování.
function buildHero(head) {
  const src = metaContent('.news-item-detail span[itemprop="image"] meta[itemprop="url"]');
  if (!src) return null;
  const h1 = document.querySelector('.news-item-detail h1');
  const figure = el('div', 'bs-post__hero');
  const img = document.createElement('img');
  img.src = src;
  img.alt = textOf(h1);
  img.loading = 'eager';
  figure.appendChild(img);
  head.appendChild(figure);
  return figure;
}

// ---------------------------------------------------------------------------
// Obsah článku — kotvy na H2 redakčního textu
// ---------------------------------------------------------------------------
function buildToc(text, after) {
  const heads = text.querySelectorAll(':scope > h2');
  if (heads.length < 2) return;

  const box = el('nav', 'bs-post__toc');
  box.setAttribute('aria-label', LABEL_TOC);
  box.appendChild(el('div', 'bs-post__toc-title', LABEL_TOC));
  const list = el('div', 'bs-post__toc-list');
  for (let i = 0; i < heads.length; i += 1) {
    const h = heads[i];
    if (!h.id) h.id = 'bs-post-h' + (i + 1);
    const row = link('#' + h.id, 'bs-post__toc-link');
    row.appendChild(el('span', 'bs-post__toc-dot'));
    row.appendChild(el('span', 'bs-post__toc-name', textOf(h)));
    list.appendChild(row);
  }
  box.appendChild(list);
  after.insertAdjacentElement('afterend', box);
}

// ---------------------------------------------------------------------------
// Prev / next — nativní odkazy mají jen obecný popisek, karty dostanou eyebrow
// ---------------------------------------------------------------------------
function buildNav(article) {
  const wrap = article.querySelector('.next-prev');
  if (!wrap) return null;
  wrap.classList.add('bs-post__nav');

  const prev = wrap.querySelector('[data-testid="buttonPreviousArticle"]');
  const next = wrap.querySelector('[data-testid="buttonNextArticle"]');
  const decorate = function (a, dir, label) {
    if (!a) return;
    a.className = 'bs-post__nav-card bs-post__nav-card--' + dir + ' is-plain';
    const mark = el('span', 'bs-post__nav-icon');
    mark.appendChild(icon(dir === 'prev' ? 'arrow-left' : 'arrow-right'));
    const lines = el('span', 'bs-post__nav-lines');
    lines.appendChild(el('span', 'bs-post__nav-eyebrow', label));
    lines.appendChild(el('span', 'bs-post__nav-title', textOf(a)));
    a.textContent = '';
    if (dir === 'prev') {
      a.appendChild(mark);
      a.appendChild(lines);
    } else {
      a.appendChild(lines);
      a.appendChild(mark);
    }
  };
  decorate(prev, 'prev', LABEL_PREV);
  decorate(next, 'next', LABEL_NEXT);
  return { prev: prev, next: next };
}

// Titulek sousedního článku Shoptet v odkazu neuvádí — doplníme ho z výpisu
// rubriky, když se v něm soused najde (jinak zůstane nativní popisek).
function fillNavTitles(nav, items) {
  if (!nav) return;
  const byHref = {};
  for (let i = 0; i < items.length; i += 1) byHref[items[i].href] = items[i].title;
  const apply = function (a) {
    if (!a) return;
    const title = byHref[a.getAttribute('href')];
    if (!title) return;
    const node = a.querySelector('.bs-post__nav-title');
    if (node) node.textContent = title;
    a.classList.remove('is-plain');
  };
  apply(nav.prev);
  apply(nav.next);
}

// ---------------------------------------------------------------------------
// Pás „Další z rubriky“ — vlastní blok z výpisu rubriky (fetch + DOMParser)
// ---------------------------------------------------------------------------
function buildMore(host, rubric, items) {
  if (!items.length || host.querySelector('.bs-post__more')) return;

  const section = el('section', 'bs-post__more');
  const head = el('div', 'bs-sechead bs-sechead--listing');
  head.appendChild(el('h2', 'bs-sechead__title', LABEL_MORE_PRE + rubric.name));
  head.appendChild(el('span', 'bs-sechead__line'));
  head.appendChild(link(rubric.href, 'bs-post__more-all', LABEL_MORE_ALL));
  section.appendChild(head);

  const track = el('div', 'bs-post__more-track');
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const card = link(item.href, 'bs-post__mcard');
    const media = el('span', 'bs-post__mcard-media');
    if (item.img) {
      const img = document.createElement('img');
      img.src = item.img;
      img.alt = item.alt || item.title;
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.appendChild(icon('photo'));
    }
    card.appendChild(media);
    const body = el('span', 'bs-post__mcard-body');
    body.appendChild(el('span', 'bs-post__mcard-rub', rubric.name));
    body.appendChild(el('span', 'bs-post__mcard-title', item.title));
    card.appendChild(body);
    track.appendChild(card);
  }
  section.appendChild(track);
  host.appendChild(section);
}

function loadRubric(host, rubric, nav) {
  if (!rubric || !window.fetch || !window.DOMParser) return;
  fetch(rubric.href, { credentials: 'same-origin' })
    .then(function (res) {
      return res.ok ? res.text() : Promise.reject(new Error(String(res.status)));
    })
    .then(function (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const here = window.location.pathname;
      const items = parseArticles(doc).filter(function (a) {
        return a.href && a.href !== here;
      });
      fillNavTitles(nav, parseArticles(doc));
      buildMore(host, rubric, items.slice(0, MORE_LIMIT));
    })
    .catch(function () {
      /* výpis rubriky nedostupný → pás vypadne, layout na něm nestojí */
    });
}

// ---------------------------------------------------------------------------

export function initArticleDetail() {
  if (!isPost()) return;
  const host = document.querySelector('.news-item-detail');
  if (!host || host.classList.contains('bs-post')) return;
  const article = host.querySelector('article');
  const head = article ? article.querySelector('header') : null;
  const text = article ? article.querySelector('.text') : null;
  if (!article || !head || !text) return;

  host.classList.add('bs-post');
  head.classList.add('bs-post__head');
  text.classList.add('bs-post__text');
  const h1 = head.querySelector('h1');
  if (h1) h1.classList.add('bs-post__title');

  const rubric = currentRubric();
  buildMeta(head, rubric, text);
  movePerex(head, text);
  buildByline(head);
  buildHero(head);
  buildToc(text, head);

  const nav = buildNav(article);

  document.documentElement.classList.add(READY);

  // pás z rubriky je doplněk — až po vykreslení zbytku stránky
  const load = function () {
    loadRubric(host, rubric, nav);
  };
  if (window.requestIdleCallback) window.requestIdleCallback(load);
  else window.setTimeout(load, 300);
}
